import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { md5 } from '@/lib/md5';
import { checkRateLimit, rateLimitMessage } from '@/lib/coreRateLimit';
import { provisionCoreForMerchant, linkUserToOrganization } from '@/lib/zifekCore';
import { setSSOCookie, clearSSOCookie } from '@/hooks/useSSO';
import { validateUsername } from '@/lib/username';
import { TYPE_COMPTE_VENDEUR } from '@/lib/marketplace';
import { fetchMarketplaceSettings } from '@/lib/marketplaceSettings';
import { normalizeCurrency } from '@/lib/currency';
import { logUserAudit } from '@/lib/userAudit';
import { findActiveCode, ensureRevendeurCode } from '@/lib/parrainage';
import { fetchSubscriptionPlans } from '@/lib/subscriptionPlans';
import type { OpeningHours } from '@/lib/restaurantHours';

export interface ZifekUser {
  id: number;
  idcommerce: number;
  user_name: string;
  email: string;
  name: string;
  nomcommerce: string;
  type: string;
  typecompte: number;
  package: number;
  monaie: string;
  langue: string;
  active: number;
  payant: number;
  theme: string;
  couleurcharte: string;
  image: string;
  imageaboutus: string;
  telephone: string;
  "Pays": string;
  "Ville": string;
  "Quartier": string;
  adresse: string;
  description: string;
  aboutus: string;
  solde: number;
  datecreation: string;
  tva: string;
  /** Horaires d'ouverture de la boutique (texte libre historique). */
  horaires: string;
  /** Horaires d'ouverture structurés par jour. */
  horaires_json: OpeningHours | null;
  /** Réseaux sociaux de la boutique. */
  lienfacebook: string;
  lieninstagram: string;
  lientiktok: string;
  lientwitter: string;
  lienlinkedin: string;
  lienyoutube: string;
  /** Appartenance au réseau Zifek Commerce (0/1). */
  reseau_zifek: number;
  /** Code de parrainage utilisé à l'inscription (le cas échéant). */
  code_parrainage: string;
  /** Identifiant du parrain (revendeur / commerce / structure). */
  parrain_id: number | null;
  /** Mois gratuits offerts par le parrainage. */
  parrainage_mois_gratuits: number;
}

export interface AuthContextType {
  user: ZifekUser | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<{ success: boolean; error?: string; user?: ZifekUser }>;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string }>;
  registerClient: (data: ClientRegisterData) => Promise<{ success: boolean; error?: string }>;
  registerVendeur: (data: VendeurRegisterData) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  updateUser: (data: Partial<ZifekUser>) => void;
}

export interface RegisterData {
  user_name: string;
  email: string;
  password: string;
  name: string;
  nomcommerce: string;
  telephone: string;
  type: string;
  typecompte: number;
  "Pays": string;
  "Ville": string;
  /** Code de parrainage facultatif saisi à l'inscription. */
  code_parrainage?: string;
}

export interface ClientRegisterData {
  user_name: string;
  email: string;
  password: string;
  name: string;
  telephone: string;
}

export interface VendeurRegisterData {
  user_name: string;
  email: string;
  password: string;
  name: string;
  nomcommerce: string;
  telephone: string;
  "Pays": string;
  "Ville": string;
  /** Identifiant du commerce (marketplace) auquel le vendeur est rattaché. */
  marketplaceId: number;
  /** Catégorie choisie par le vendeur (catégories produits/services de la marketplace). */
  categorieId?: number | null;
  categorieNom?: string | null;
  /** Sous-catégorie choisie (si la catégorie en possède). */
  souscategorieId?: number | null;
  souscategorieNom?: string | null;
}

export const AuthContext = createContext<AuthContextType | null>(null);

const STORAGE_KEY = 'zifek_user';

function setStoredUser(user: ZifekUser | null) {
  if (user) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

function getStoredUser(): ZifekUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function mapZifekUser(row: Record<string, unknown>): ZifekUser {
  return {
    id: row.id as number,
    idcommerce: (row.idcommerce as number) || 0,
    user_name: row.user_name as string,
    email: row.email as string,
    name: row.name as string,
    nomcommerce: (row.nomcommerce as string) || '',
    type: row.type as string,
    typecompte: row.typecompte as number,
    package: row.package as number,
    monaie: normalizeCurrency(row.monaie as string),
    langue: row.langue as string,
    active: row.active as number,
    payant: (row.payant as number) || 0,
    theme: (row.theme as string) || '',
    couleurcharte: (row.couleurcharte as string) || '',
    image: (row.image as string) || '',
    imageaboutus: (row.imageaboutus as string) || '',
    telephone: (row.telephone as string) || '',
    "Pays": (row["Pays"] as string) || '',
    "Ville": (row["Ville"] as string) || '',
    "Quartier": (row["Quartier"] as string) || '',
    adresse: (row.adresse as string) || '',
    description: (row.description as string) || '',
    aboutus: (row.aboutus as string) || '',
    solde: (row.solde as number) || 0,
    datecreation: (row.datecreation as string) || '',
    tva: (row.tva as string) || '',
    horaires: (row.horaires as string) || '',
    horaires_json: (row.horaires_json as OpeningHours | null) || null,
    lienfacebook: (row.lienfacebook as string) || '',
    lieninstagram: (row.lieninstagram as string) || '',
    lientiktok: (row.lientiktok as string) || '',
    lientwitter: (row.lientwitter as string) || '',
    lienlinkedin: (row.lienlinkedin as string) || '',
    lienyoutube: (row.lienyoutube as string) || '',
    reseau_zifek: (row.reseau_zifek as number) || 0,
    code_parrainage: (row.code_parrainage as string) || '',
    parrain_id: row.parrain_id != null ? Number(row.parrain_id) : null,
    parrainage_mois_gratuits: (row.parrainage_mois_gratuits as number) || 0,
  };
}

/**
 * Un compte est considéré comme super admin quand son typecompte vaut 0 ou 1.
 * Ainsi, quel que soit le lien de connexion (client ou pro), ces comptes ont
 * accès au superadmin.
 */
export function isSuperAdmin(typecompte: number): boolean {
  return typecompte === 0 || typecompte === 1;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ZifekUser | null>(getStoredUser);
  const [loading, setLoading] = useState(false);
  const [ssoChecked, setSSOChecked] = useState(false);

  // ── SSO restore: check shared cookie on mount if no localStorage session ──
  useEffect(() => {
    if (ssoChecked) return;
    const stored = getStoredUser();
    if (stored) {
      setSSOChecked(true);
      return;
    }

    // Dynamic import to avoid bundling SSO code if not needed
    import('@/hooks/useSSO').then(({ getSSOCookie }) => {
      const sso = getSSOCookie();
      if (sso && sso.i) {
        supabase
          .from('users')
          .select('*')
          .eq('id', sso.i)
          .eq('active', 1)
          .maybeSingle()
          .then(({ data: foundUser }) => {
            if (foundUser) {
              const zifekUser = mapZifekUser(foundUser);
              setUser(zifekUser);
              setStoredUser(zifekUser);
            } else {
              clearSSOCookie();
            }
          });
      }
      setSSOChecked(true);
    }).catch(() => {
      setSSOChecked(true);
    });
  }, [ssoChecked]);

  const login = useCallback(async (identifier: string, password: string) => {
    setLoading(true);
    try {
      // Rate limiting applicatif (spec 64) : protège la connexion contre le
      // brute-force, par identifiant (sujet « ip » de la politique auth).
      const rl = await checkRateLimit({
        organizationId: null,
        scope: 'auth',
        subjectType: 'ip',
        subjectKey: `auth:${(identifier || '').toLowerCase()}`,
        endpoint: 'auth.login',
      });
      if (!rl.allowed) return { success: false, error: rateLimitMessage(rl) };

      const { data: users, error } = await supabase
        .from('users')
        .select('*')
        .or(`email.eq.${identifier},user_name.eq.${identifier}`)
        .eq('active', 1)
        .limit(1);

      if (error || !users || users.length === 0) {
        return { success: false, error: 'Identifiant ou mot de passe incorrect.' };
      }

      const foundUser = users[0];
      const storedHash = foundUser.password;
      let passwordOk = false;

      // Check if it's a bcrypt hash (starts with $2)
      if (storedHash.startsWith('$2')) {
        const { default: bcrypt } = await import('bcryptjs');
        passwordOk = bcrypt.compareSync(password, storedHash);
      } else {
        // Legacy MD5 comparison
        const hashedInput = md5(password);
        passwordOk = hashedInput === storedHash;

        // If MD5 matches, upgrade to bcrypt for future logins
        if (passwordOk) {
          const { default: bcrypt } = await import('bcryptjs');
          const salt = bcrypt.genSaltSync(10);
          const newHash = bcrypt.hashSync(password, salt);
          await supabase
            .from('users')
            .update({ password: newHash })
            .eq('id', foundUser.id);
        }
      }

      if (!passwordOk) {
        return { success: false, error: 'Identifiant ou mot de passe incorrect.' };
      }

      const zifekUser = mapZifekUser(foundUser);

      setUser(zifekUser);
      setStoredUser(zifekUser);
      // SSO: set shared cookie so all subdomains know about this session
      setSSOCookie(zifekUser.id, zifekUser.email);
      // 🔎 Journal d'activité : connexion réussie.
      void logUserAudit({
        userId: zifekUser.id,
        category: 'login',
        action: 'login',
        description: `Connexion réussie${zifekUser.type ? ` (${zifekUser.type})` : ''}`,
        metadata: { typecompte: zifekUser.typecompte, email: zifekUser.email },
      });
      return { success: true, user: zifekUser };
    } catch (err: unknown) {
      return { success: false, error: 'Erreur de connexion. Veuillez réessayer.' };
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    setLoading(true);
    try {
      // ── Validation du nom d'utilisateur (sous-domaine) ──
      const { valid, normalized, error: usernameError } = validateUsername(data.user_name);
      if (!valid) {
        return { success: false, error: usernameError || 'Nom d\u2019utilisateur invalide.' };
      }

      // Check if email already exists
      const { data: existing } = await supabase
        .from('users')
        .select('id')
        .eq('email', data.email);

      if (existing && existing.length > 0) {
        return { success: false, error: 'Cet email est déjà utilisé.' };
      }

      // Check if username already exists (insensible à la casse)
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .ilike('user_name', normalized);

      if (existingUser && existingUser.length > 0) {
        return { success: false, error: "Ce nom d'utilisateur est déjà pris." };
      }

      // Get default theme from zifek settings
      let defaultThemeId = 1;
      let defaultThemeName = 'Business Moderne';
      try {
        const { data: zifekSettings } = await supabase
          .from('zifek')
          .select('default_theme_id')
          .limit(1)
          .maybeSingle();
        
        if (zifekSettings?.default_theme_id) {
          const themeId = parseInt(String(zifekSettings.default_theme_id), 10);
          if (!isNaN(themeId) && themeId > 0) {
            defaultThemeId = themeId;
            // Fetch theme name
            const { data: themeData } = await supabase
              .from('sitewebtheme')
              .select('titre')
              .eq('id', themeId)
              .maybeSingle();
            if (themeData?.titre) {
              defaultThemeName = themeData.titre;
            }
          }
        }
      } catch { /* fallback to theme 1 */ }

      // Hash password with bcrypt
      const { default: bcrypt } = await import('bcryptjs');
      const salt = bcrypt.genSaltSync(10);
      const hashedPassword = bcrypt.hashSync(data.password, salt);

      const { data: newUsers, error } = await supabase
        .from('users')
        .insert({
          user_name: normalized,
          email: data.email,
          password: hashedPassword,
          name: data.name,
          nomcommerce: data.nomcommerce,
          telephone: data.telephone,
          type: data.type,
          typecompte: data.typecompte,
          "Pays": data["Pays"],
          "Ville": data["Ville"],
          monaie: 'MAD',
          langue: 'fr',
          active: 1,
          package: 0,
          payant: 1,
          theme: String(defaultThemeId),
        })
        .select('*');

      if (error) {
        return { success: false, error: "Erreur lors de l'inscription. Veuillez réessayer." };
      }

      const newUser = newUsers?.[0];
      if (newUser) {
        // Assign default theme
        await supabase
          .from('sitewebthemeactuelle')
          .insert({
            idtheme: defaultThemeId,
            nomtheme: defaultThemeName,
            idcommerce: newUser.id,
            prix: '0',
            status: 'active',
          });

        // Note: le sous-domaine (username.zifek.fr) est dérivé dynamiquement
        // depuis user_name (voir buildSubdomain / useTenant), il n'est donc
        // PAS stocké dans websitedomain. Cette table est réservée aux
        // domaines personnalisés liés par l'utilisateur.

        // Zifek Core — provisionne le socle multi-tenant (best-effort).
        await provisionCoreForMerchant({
          userId: newUser.id,
          commercialName: data.nomcommerce,
          displayName: data.name,
          userName: normalized,
          businessType: data.type,
          activityCategory: data.type,
          email: data.email,
          phone: data.telephone,
          country: data['Pays'],
          city: data['Ville'],
          currency: 'MAD',
          language: 'fr',
        });

        // Parrainage : résolution du code saisi et enregistrement du parrain.
        if (data.code_parrainage && data.code_parrainage.trim()) {
          const refCode = await findActiveCode(data.code_parrainage);
          if (refCode) {
            await supabase
              .from('users')
              .update({
                code_parrainage: refCode.code,
                parrain_id: refCode.ownerId ?? null,
                parrainage_mois_gratuits: refCode.moisGratuits,
              })
              .eq('id', newUser.id);
            newUser.code_parrainage = refCode.code;
            newUser.parrain_id = refCode.ownerId ?? null;
            newUser.parrainage_mois_gratuits = refCode.moisGratuits;
          }
        }

        // Compte revendeur : génération automatique de son code de parrainage.
        void ensureRevendeurCode(newUser.id, data.type, data.nomcommerce);

        // Abonnement Zifek par défaut : chaque nouveau client reçoit la formule
        // d'entrée (ex. 35 MAD), avec la période d'essai (mois gratuits) définie
        // par le SuperAdmin sur cette formule. Best-effort : n'interrompt jamais
        // l'inscription.
        try {
          const plans = await fetchSubscriptionPlans({ activeOnly: true });
          const defaultPlan = plans
            .filter((p) => !p.personnalise && p.prix > 0)
            .sort((a, b) => a.ordre - b.ordre || a.prix - b.prix)[0];
          if (defaultPlan) {
            const trialMonths = Math.max(0, defaultPlan.moisGratuits || 0);
            const start = new Date();
            const end = new Date(start);
            end.setMonth(end.getMonth() + trialMonths);
            const nextBilling = new Date(start);
            nextBilling.setMonth(nextBilling.getMonth() + (trialMonths > 0 ? trialMonths : 1));
            const iso = (d: Date) => d.toISOString().slice(0, 10);

            // Rattache la formule au compte.
            await supabase.from('users').update({ package: defaultPlan.id }).eq('id', newUser.id);
            newUser.package = defaultPlan.id;

            // Activation du pack + période d'essai.
            await supabase.from('user_packs').insert({
              user_id: newUser.id,
              pack_id: defaultPlan.id,
              date_debut: iso(start),
              date_fin: iso(end),
              statut: trialMonths > 0 ? 'essai' : 'actif',
            });

            // Historique d'abonnement (visible dans « Mon abonnement »).
            await supabase.from('package_subscriptions').insert({
              user_id: newUser.id,
              plan: defaultPlan.nom,
              period: 'monthly',
              prix: defaultPlan.prix,
              monaie: 'MAD',
              start_date: iso(start),
              next_billing_date: iso(nextBilling),
              status: trialMonths > 0 ? 'trial' : 'pending',
              note: trialMonths > 0 ? `Période d'essai : ${trialMonths} mois offert(s)` : 'Abonnement Zifek',
            });
          }
        } catch { /* best-effort : ne bloque pas l'inscription */ }
      }

      // Auto-login: set user in context
      if (newUser) {
        const zifekUser = mapZifekUser(newUser);
        setUser(zifekUser);
        setStoredUser(zifekUser);
        setSSOCookie(zifekUser.id, zifekUser.email);
        void logUserAudit({
          userId: zifekUser.id,
          category: 'account',
          action: 'account_created',
          description: 'Création du compte marchand',
          metadata: { typecompte: zifekUser.typecompte, email: zifekUser.email, commerce: data.nomcommerce },
        });
      }

      return { success: true };
    } catch (err: unknown) {
      return { success: false, error: "Erreur lors de l'inscription. Veuillez réessayer." };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUser = useCallback((data: Partial<ZifekUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...data };
      setStoredUser(updated);
      return updated;
    });
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setStoredUser(null);
    clearSSOCookie();
  }, []);

  const registerClient = useCallback(async (data: ClientRegisterData) => {
    setLoading(true);
    try {
      // ── Validation du nom d'utilisateur (sous-domaine) ──
      const { valid, normalized, error: usernameError } = validateUsername(data.user_name);
      if (!valid) {
        return { success: false, error: usernameError || 'Nom d\u2019utilisateur invalide.' };
      }

      // Check if email already exists
      const { data: existing } = await supabase
        .from('users')
        .select('id')
        .eq('email', data.email);

      if (existing && existing.length > 0) {
        return { success: false, error: 'Cet email est déjà utilisé.' };
      }

      // Check if username already exists (insensible à la casse)
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .ilike('user_name', normalized);

      if (existingUser && existingUser.length > 0) {
        return { success: false, error: "Ce nom d'utilisateur est déjà pris." };
      }

      // Hash password with bcrypt
      const { default: bcrypt } = await import('bcryptjs');
      const salt = bcrypt.genSaltSync(10);
      const hashedPassword = bcrypt.hashSync(data.password, salt);

      const { data: newUsers, error } = await supabase
        .from('users')
        .insert({
          user_name: normalized,
          email: data.email,
          password: hashedPassword,
          name: data.name,
          telephone: data.telephone || '',
          type: 'Client',
          typecompte: 6,
          active: 1,
          package: 0,
          payant: 0,
          monaie: 'MAD',
          langue: 'fr',
          etatsousdomaine: 0,
        })
        .select('*');

      if (error) {
        return { success: false, error: "Erreur lors de l'inscription. Veuillez réessayer." };
      }

      const newUser = newUsers?.[0];
      if (newUser) {
        const zifekUser = mapZifekUser(newUser);
        setUser(zifekUser);
        setStoredUser(zifekUser);
        setSSOCookie(zifekUser.id, zifekUser.email);
        void logUserAudit({
          userId: zifekUser.id,
          category: 'account',
          action: 'account_created',
          description: 'Création du compte client',
          metadata: { typecompte: zifekUser.typecompte, email: zifekUser.email },
        });
      }

      return { success: true };
    } catch (err: unknown) {
      return { success: false, error: "Erreur lors de l'inscription. Veuillez réessayer." };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Inscription d'un vendeur sur une marketplace :
   * 1. crée le compte utilisateur (typecompte vendeur)
   * 2. l'attache à la marketplace via la table `userwebsite`
   * Le compte n'a PAS de site propre : sur un autre site Zifek il se comporte
   * comme un utilisateur normal (typecompte non marchand).
   */
  const registerVendeur = useCallback(async (data: VendeurRegisterData) => {
    setLoading(true);
    try {
      const { valid, normalized, error: usernameError } = validateUsername(data.user_name);
      if (!valid) {
        return { success: false, error: usernameError || 'Nom d\u2019utilisateur invalide.' };
      }

      const { data: existingEmail } = await supabase
        .from('users')
        .select('id')
        .eq('email', data.email);
      if (existingEmail && existingEmail.length > 0) {
        return { success: false, error: 'Cet email est déjà utilisé.' };
      }

      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .ilike('user_name', normalized);
      if (existingUser && existingUser.length > 0) {
        return { success: false, error: "Ce nom d'utilisateur est déjà pris." };
      }

      const { default: bcrypt } = await import('bcryptjs');
      const salt = bcrypt.genSaltSync(10);
      const hashedPassword = bcrypt.hashSync(data.password, salt);

      const { data: newUsers, error } = await supabase
        .from('users')
        .insert({
          user_name: normalized,
          email: data.email,
          password: hashedPassword,
          name: data.name,
          nomcommerce: data.nomcommerce,
          telephone: data.telephone || '',
          type: 'Vendeur',
          typecompte: TYPE_COMPTE_VENDEUR,
          "Pays": data["Pays"],
          "Ville": data["Ville"],
          monaie: 'MAD',
          langue: 'fr',
          active: 1,
          package: 0,
          payant: 0,
        })
        .select('*');

      if (error || !newUsers?.[0]) {
        return { success: false, error: "Erreur lors de l'inscription. Veuillez réessayer." };
      }

      const newUser = newUsers[0];

      // Rattachement du vendeur à la marketplace
      if (data.marketplaceId) {
        // La marketplace peut approuver ses vendeurs automatiquement ou exiger
        // une validation manuelle (réglage dans dashboard/marketplace).
        const settings = await fetchMarketplaceSettings(data.marketplaceId);
        const validation = settings.autoApproveVendors ? 'approuve' : 'en_attente';
        await supabase.from('userwebsite').insert({
          idcommerce: data.marketplaceId,
          iduser: newUser.id,
          typecompte: TYPE_COMPTE_VENDEUR,
          user_name: normalized,
          image: '',
          statut: 1,
          validation,
          categorie_id: data.categorieId ?? null,
          categorie_nom: data.categorieNom ?? null,
          souscategorie_id: data.souscategorieId ?? null,
          souscategorie_nom: data.souscategorieNom ?? null,
        });

        // Zifek Core — rattache le vendeur à l'organisation de la marketplace.
        await linkUserToOrganization(newUser.id, data.marketplaceId, 'vendor', 'Vendeur');
      }

      const zifekUser = mapZifekUser(newUser);
      setUser(zifekUser);
      setStoredUser(zifekUser);
      setSSOCookie(zifekUser.id, zifekUser.email);
      void logUserAudit({
        userId: zifekUser.id,
        category: 'account',
        action: 'account_created',
        description: 'Création du compte vendeur',
        metadata: { typecompte: zifekUser.typecompte, email: zifekUser.email, marketplaceId: data.marketplaceId },
      });

      return { success: true };
    } catch (err: unknown) {
      return { success: false, error: "Erreur lors de l'inscription. Veuillez réessayer." };
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, registerClient, registerVendeur, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}